import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  LayoutDashboard, FileQuestion, ClipboardList, BarChart3, 
  Shield, LogOut, Users, Eye, Award, Clock, Target,
  TrendingUp, CheckCircle, XCircle, Activity, MessageSquare,
  AlertTriangle, ChevronDown, ChevronUp, Download, FileDown
} from "lucide-react";
import { generateExamReport, generateCandidateReport } from "../utils/pdfGenerator";

const ResultsAnalytics = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  
  const [results, setResults] = useState([]);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterExam, setFilterExam] = useState("all");
  const [detailedResult, setDetailedResult] = useState(null);
  const [proctoringLogs, setProctoringLogs] = useState([]);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(null);

  useEffect(() => { fetchData(); }, [filterExam]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [resultsRes, examsRes] = await Promise.all([
        axios.get(`${API}/admin/results${filterExam !== 'all' ? `?exam_id=${filterExam}` : ''}`, { headers }),
        axios.get(`${API}/exams`, { headers })
      ]);
      setResults(resultsRes.data);
      setExams(examsRes.data);
    } catch (error) {
      toast.error("Failed to fetch results");
    } finally {
      setLoading(false);
    }
  };

  const downloadExamReport = async () => {
    if (filterExam === "all") {
      toast.error("Please select a specific exam to generate the report");
      return;
    }
    setGeneratingPdf("exam");
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const exam = exams.find(e => e.id === filterExam);
      const [topicRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/topic-performance?exam_id=${filterExam}`, { headers }).catch(() => ({ data: [] }))
      ]);
      generateExamReport(exam, results, topicRes.data);
      toast.success("Exam report PDF downloaded!");
    } catch (error) {
      toast.error("Failed to generate exam report");
    } finally {
      setGeneratingPdf(null);
    }
  };

  const downloadCandidateReport = async (result) => {
    setGeneratingPdf(result.id);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const detailRes = await axios.get(`${API}/admin/results/${result.id}`, { headers });
      generateCandidateReport({ ...detailRes.data, rank: result.rank });
      toast.success(`Report for ${result.candidate_name} downloaded!`);
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Failed to generate candidate report");
    } finally {
      setGeneratingPdf(null);
    }
  };

  const viewDetails = async (result) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [detailRes, procRes] = await Promise.all([
        axios.get(`${API}/admin/results/${result.id}`, { headers }),
        axios.get(`${API}/admin/proctoring-logs/${result.id}`, { headers }).catch(() => ({ data: [] }))
      ]);
      setDetailedResult(detailRes.data);
      setProctoringLogs(procRes.data);
      setShowDetailDialog(true);
      setExpandedQuestion(null);
    } catch (error) {
      toast.error("Failed to fetch details");
    }
  };

  const handleLogout = () => { logout(); navigate("/auth"); };

  const navItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/admin" },
    { icon: FileQuestion, label: "Question Bank", path: "/admin/questions" },
    { icon: ClipboardList, label: "Exams", path: "/admin/exams" },
    { icon: BarChart3, label: "Results", path: "/admin/results", active: true },
    { icon: Users, label: "Organisers", path: "/admin/organisers" },
    { icon: MessageSquare, label: "Feedback", path: "/admin/feedback" },
    { icon: Activity, label: "IP Logs", path: "/admin/logs" }
  ];

  const getRankBadge = (rank) => {
    const cls = rank === 1 ? 'rank-1' : rank === 2 ? 'rank-2' : rank === 3 ? 'rank-3' : 'rank-other';
    return <span className={`rank-badge ${cls}`}>#{rank}</span>;
  };

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="results-analytics">
      <aside className="sidebar">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-blue-500 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>edSolutions360</span>
              <p className="text-xs text-slate-500">Examination Portal</p>
            </div>
          </div>
          <nav className="space-y-2">
            {navItems.map((item) => (
              <div key={item.path} className={`nav-item ${item.active ? 'active' : ''}`} onClick={() => navigate(item.path)}>
                <item.icon className="w-5 h-5" /><span>{item.label}</span>
              </div>
            ))}
          </nav>
        </div>
        <div className="mt-auto p-6 border-t border-slate-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center"><Users className="w-5 h-5 text-indigo-600" /></div>
            <div>
              <p className="text-sm font-medium text-slate-900">{user?.full_name}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
          </div>
          <Button variant="outline" className="w-full" onClick={handleLogout}><LogOut className="w-4 h-4 mr-2" />Logout</Button>
        </div>
      </aside>

      <main className="main-content p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Results & Analytics</h1>
              <p className="text-slate-600 mt-1">{results.length} exam attempts</p>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                onClick={downloadExamReport} 
                disabled={filterExam === "all" || generatingPdf === "exam"}
                className="border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                data-testid="download-exam-report-btn"
              >
                <FileDown className="w-4 h-4 mr-2" />
                {generatingPdf === "exam" ? "Generating..." : "Exam Report PDF"}
              </Button>
              <Select value={filterExam} onValueChange={setFilterExam}>
                <SelectTrigger className="w-60" data-testid="filter-exam-select"><SelectValue placeholder="Filter by exam" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Exams</SelectItem>
                  {exams.map((exam) => <SelectItem key={exam.id} value={exam.id}>{exam.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="spinner" /></div>
          ) : results.length === 0 ? (
            <Card className="text-center py-12">
              <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-slate-900 mb-2">No Results Yet</h4>
              <p className="text-slate-500">Results appear here once candidates complete their tests.</p>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr><th>Rank</th><th>Candidate</th><th>Exam</th><th>Score</th><th>Accuracy</th><th>Hard Q</th><th>Time</th><th>Recommendation</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                      {results.map((result, index) => {
                        const hardData = result.difficulty_breakdown?.hard || { total: 0, correct: 0 };
                        const hardAcc = hardData.total > 0 ? Math.round((hardData.correct / hardData.total) * 100) : 0;
                        const flags = result.proctoring_flags || {};
                        const totalFlags = Object.values(flags).reduce((a, b) => a + b, 0);
                        const rec = result.recommendation || "—";
                        const recColor = rec === "Strongly Recommended" ? "text-emerald-700 bg-emerald-50" : rec === "Recommended" ? "text-blue-700 bg-blue-50" : rec === "Consider" ? "text-amber-700 bg-amber-50" : "text-red-700 bg-red-50";
                        return (
                          <tr key={result.id} data-testid={`result-row-${index}`}>
                            <td>{getRankBadge(result.rank)}</td>
                            <td>
                              <div><p className="font-medium">{result.candidate_name}</p><p className="text-sm text-slate-500">{result.candidate_email}</p></div>
                            </td>
                            <td><Badge variant="outline">{result.exam_name}</Badge></td>
                            <td>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold">{result.score}</span>
                                <div className="w-16 h-2 bg-slate-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${Math.min(100, result.accuracy)}%` }} />
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className={`font-medium ${result.accuracy >= 80 ? 'text-emerald-600' : result.accuracy >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
                                {result.accuracy}%
                              </span>
                            </td>
                            <td><span className={`font-medium ${hardAcc >= 70 ? 'text-emerald-600' : hardAcc >= 40 ? 'text-amber-600' : 'text-red-600'}`}>{hardAcc}%</span></td>
                            <td><span className="flex items-center gap-1 text-slate-600"><Clock className="w-4 h-4" />{Math.floor(result.time_taken_seconds / 60)}m {result.time_taken_seconds % 60}s</span></td>
                            <td>
                              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${recColor}`}>
                                {rec}
                              </span>
                              {totalFlags > 0 && (
                                <span className="ml-1 inline-flex items-center gap-0.5 text-amber-600 text-xs" title="Proctoring flags">
                                  <AlertTriangle className="w-3 h-3" />{totalFlags}
                                </span>
                              )}
                            </td>
                            <td>
                              <div className="flex items-center gap-1">
                                <Button variant="ghost" size="sm" onClick={() => viewDetails(result)} data-testid={`view-details-${index}`}>
                                  <Eye className="w-4 h-4 mr-1" />Details
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => downloadCandidateReport(result)} 
                                  disabled={generatingPdf === result.id}
                                  className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                                  data-testid={`download-candidate-report-${index}`}
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Enhanced Detail Dialog with Question Review */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Candidate Report</DialogTitle>
              {detailedResult && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => { generateCandidateReport(detailedResult); toast.success("PDF downloaded!"); }}
                  className="border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                  data-testid="dialog-download-report-btn"
                >
                  <Download className="w-4 h-4 mr-2" />Download PDF
                </Button>
              )}
            </div>
          </DialogHeader>
          {detailedResult && (
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="questions" data-testid="questions-tab">Questions & Explanations</TabsTrigger>
                <TabsTrigger value="activity">Activity & Proctoring</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-6 mt-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div>
                    <h3 className="font-semibold text-lg">{detailedResult.candidate_name}</h3>
                    <p className="text-slate-500">{detailedResult.candidate_email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-500">IP Address</p>
                    <p className="font-mono text-slate-900">{detailedResult.ip_address}</p>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4">
                  <Card className="p-4"><div className="flex items-center gap-3"><Award className="w-8 h-8 text-indigo-600" /><div><p className="text-2xl font-bold">{detailedResult.score}</p><p className="text-xs text-slate-500">Total Score</p></div></div></Card>
                  <Card className="p-4"><div className="flex items-center gap-3"><Target className="w-8 h-8 text-emerald-600" /><div><p className="text-2xl font-bold">{detailedResult.accuracy}%</p><p className="text-xs text-slate-500">Accuracy</p></div></div></Card>
                  <Card className="p-4"><div className="flex items-center gap-3"><CheckCircle className="w-8 h-8 text-blue-600" /><div><p className="text-2xl font-bold">{detailedResult.correct_answers}/{detailedResult.total_questions}</p><p className="text-xs text-slate-500">Correct</p></div></div></Card>
                  <Card className="p-4"><div className="flex items-center gap-3"><Clock className="w-8 h-8 text-amber-600" /><div><p className="text-2xl font-bold">{Math.floor(detailedResult.time_taken_seconds / 60)}m</p><p className="text-xs text-slate-500">Time Taken</p></div></div></Card>
                </div>

                <Card>
                  <CardHeader><CardTitle className="text-base">Performance by Difficulty</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {Object.entries(detailedResult.difficulty_breakdown || {}).map(([diff, data]) => {
                        const pct = data.total > 0 ? (data.correct / data.total) * 100 : 0;
                        return (
                          <div key={diff} className="flex items-center gap-4">
                            <span className={`w-16 capitalize font-medium ${diff === 'easy' ? 'text-emerald-600' : diff === 'medium' ? 'text-amber-600' : 'text-red-600'}`}>{diff}</span>
                            <div className="flex-1"><Progress value={pct} className="h-3" /></div>
                            <span className="w-24 text-right text-sm">{data.correct} / {data.total} ({pct.toFixed(0)}%)</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Questions & Explanations Tab */}
              <TabsContent value="questions" className="space-y-4 mt-4">
                <p className="text-sm text-slate-500 mb-2">Click on a question to see the explanation</p>
                {detailedResult.questions?.map((q, i) => {
                  const candidateAnswer = detailedResult.answers?.[String(i)];
                  const isCorrect = candidateAnswer === q.correct_answer;
                  const isExpanded = expandedQuestion === i;
                  return (
                    <Card key={i} className={`border-l-4 ${isCorrect ? 'border-l-emerald-500' : candidateAnswer !== undefined ? 'border-l-red-500' : 'border-l-slate-300'}`} data-testid={`question-review-${i}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between cursor-pointer" onClick={() => setExpandedQuestion(isExpanded ? null : i)}>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs font-medium text-slate-400">Q{i + 1}</span>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${q.difficulty === 'easy' ? 'badge-easy' : q.difficulty === 'medium' ? 'badge-medium' : 'badge-hard'}`}>{q.difficulty}</span>
                              <span className="text-xs text-slate-500">{q.topic}</span>
                              {isCorrect ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : candidateAnswer !== undefined ? <XCircle className="w-4 h-4 text-red-500" /> : <span className="text-xs text-slate-400">Skipped</span>}
                            </div>
                            <p className="text-sm text-slate-800">{q.question_text}</p>
                          </div>
                          {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                        </div>

                        {isExpanded && (
                          <div className="mt-4 space-y-3 animate-fade-in">
                            <div className="grid grid-cols-2 gap-2">
                              {q.options.map((opt, idx) => (
                                <div key={idx} className={`text-sm p-2 rounded-lg border ${
                                  idx === q.correct_answer ? 'bg-emerald-50 text-emerald-700 border-emerald-300' :
                                  idx === candidateAnswer && idx !== q.correct_answer ? 'bg-red-50 text-red-700 border-red-300' :
                                  'bg-slate-50 text-slate-600 border-slate-200'
                                }`}>
                                  <span className="font-medium mr-2">{String.fromCharCode(65 + idx)}.</span>
                                  {opt}
                                  {idx === q.correct_answer && <CheckCircle className="w-3 h-3 inline ml-1 text-emerald-500" />}
                                  {idx === candidateAnswer && idx !== q.correct_answer && <XCircle className="w-3 h-3 inline ml-1 text-red-500" />}
                                </div>
                              ))}
                            </div>
                            {q.explanation && (
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <p className="text-sm text-blue-800"><span className="font-semibold">Explanation:</span> {q.explanation}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </TabsContent>

              {/* Activity & Proctoring Tab */}
              <TabsContent value="activity" className="space-y-4 mt-4">
                {/* Proctoring Flags Summary */}
                {detailedResult.proctoring_flags && Object.keys(detailedResult.proctoring_flags).length > 0 && (
                  <Card className="border-amber-200 bg-amber-50/50">
                    <CardHeader><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-600" />Proctoring Flags</CardTitle></CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {Object.entries(detailedResult.proctoring_flags).map(([type, count]) => (
                          <div key={type} className="bg-white rounded-lg p-3 border border-amber-200">
                            <p className="text-xs text-slate-500 capitalize">{type.replace(/_/g, ' ')}</p>
                            <p className="text-2xl font-bold text-amber-700">{count}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Proctoring Event Timeline */}
                {proctoringLogs.length > 0 && (
                  <Card>
                    <CardHeader><CardTitle className="text-base">Proctoring Timeline</CardTitle></CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {proctoringLogs.map((log, idx) => (
                          <div key={idx} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
                            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                              <AlertTriangle className="w-4 h-4 text-amber-600" />
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium capitalize">{log.event_type.replace(/_/g, ' ')}</p>
                              {log.details && <p className="text-xs text-slate-500">{log.details}</p>}
                            </div>
                            <span className="text-xs text-slate-500">{new Date(log.timestamp).toLocaleTimeString()}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* IP Activity Logs */}
                {detailedResult.ip_logs?.length > 0 && (
                  <Card>
                    <CardHeader><CardTitle className="text-base">Activity Log</CardTitle></CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {detailedResult.ip_logs.map((log, idx) => (
                          <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${log.action === 'login' ? 'bg-blue-50' : log.action === 'register' ? 'bg-emerald-50' : log.action === 'exam_start' ? 'bg-amber-50' : 'bg-indigo-50'}`}>
                                <Activity className={`w-4 h-4 ${log.action === 'login' ? 'text-blue-600' : log.action === 'register' ? 'text-emerald-600' : log.action === 'exam_start' ? 'text-amber-600' : 'text-indigo-600'}`} />
                              </div>
                              <div><p className="font-medium capitalize">{log.action.replace('_', ' ')}</p><p className="text-xs text-slate-500">{new Date(log.timestamp).toLocaleString()}</p></div>
                            </div>
                            <span className="font-mono text-sm text-slate-600">{log.ip_address}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {!detailedResult.ip_logs?.length && !proctoringLogs.length && (
                  <div className="text-center py-8 text-slate-500">No activity data available</div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResultsAnalytics;
